import React, { useState, useEffect } from 'react';
import { Printer, Loader2, FileDown, List, X } from 'lucide-react';
import { api, API_BASE } from '../utils/api';
import { formatDateThai, printReport, formatDays, formatTime, getStatusColor } from '../utils/helpers';

const SummaryReport = ({ user }) => {
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  
  const [downloadLoading, setDownloadLoading] = useState({
    active: false,
    employeeId: null,
    type: null
  });

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentDetailEmployee, setCurrentDetailEmployee] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadReports();
  }, [dateRange, selectedEmployee]);

  useEffect(() => {
    let newFilteredReports = [...allReports];
    if (filterMode === 'late_gt_4') {
      newFilteredReports = newFilteredReports.filter(r => (r.late_days || 0) > 4);
    } else if (filterMode === 'leave_gt_3') {
      newFilteredReports = newFilteredReports.filter(r => ((r.sick_leave_times || 0) + (r.personal_leave_times || 0)) > 3);
    }
    setReports(newFilteredReports);
  }, [allReports, filterMode]);

  const loadInitialData = async () => {
    try {
      // ⚠️ Backend จะส่งข้อมูลกลับมาเฉพาะโรงเรียนของแอดมินคนนี้เท่านั้น
      const [employeesData, organizationData] = await Promise.all([
        api.getEmployees(),
        api.getOrganizations()
      ]);
      setEmployees(employeesData.filter(e => e.is_active == 1 && e.employee_id !== 'ADMIN'));
      setOrganization(organizationData[0] || null);
    } catch (error) { console.error(error); }
    loadReports();
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      let data = await api.getReports({ type: 'summary', date_from: dateRange.from, date_to: dateRange.to, employee_id: selectedEmployee });
      data = data.filter(r => r.employee_id !== 'ADMIN');
      data.sort((a, b) => (a.department || '').localeCompare(b.department || '', 'th') || a.employee_id.localeCompare(b.employee_id));
      setAllReports(data);
    } catch (error) { console.error(error); setAllReports([]); } 
    finally { setLoading(false); }
  };

  // --- [Helper] ส่วนลายเซ็นมาตรฐาน ---
  const getSignatureSection = () => {
    const statHeadName = organization?.report_stat_head_name || '........................................';
    const statHeadPos = organization?.report_stat_head_position || 'เจ้าหน้าที่งานสถิติ';
    const vpName = organization?.report_vp_name || '........................................';
    const vpPos = organization?.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล';

    return `
      <div class="signature-section" style="margin-top: 40px; page-break-inside: avoid;">
        <table style="width: 100%; border: none; margin-bottom: 0;">
          <tr style="border: none; background-color: transparent;">
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${statHeadName})</p>
              <p>${statHeadPos}</p>
            </td>
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${vpName})</p>
              <p>${vpPos}</p>
            </td>
          </tr>
          <tr style="border: none; background-color: transparent;">
             <td colspan="2" style="border: none; text-align: center; padding-top: 20px;">
                วันที่ ${formatDateThai(new Date())}
             </td>
          </tr>
        </table>
      </div>
    `;
  };

  // --- 1. พิมพ์รายงานสรุป (ตารางรวม) ---
  const generatePrintReport = () => {
    if (reports.length === 0) return alert('ไม่มีข้อมูลสำหรับพิมพ์');
    
    const dateTitle = dateRange.from === dateRange.to ? formatDateThai(dateRange.from) : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`;
    const logoHtml = organization?.logo ? `<img src="${API_BASE}${organization.logo}" class="logo">` : '';
    
    const tableRows = reports.map((r, i) => `
      <tr>
        <td style="text-align: center;">${i + 1}</td>
        <td>${r.first_name} ${r.last_name}</td>
        <td>${r.department || '-'}</td>
        <td style="text-align: center;" class="${r.late_days > 0 ? 'text-red' : ''}">${r.late_days || 0}</td>
        <td style="text-align: center;">${r.sick_leave_times || 0}</td>
        <td style="text-align: center;">${formatDays(r.sick_leave_days)}</td>
        <td style="text-align: center;">${r.personal_leave_times || 0}</td>
        <td style="text-align: center;">${formatDays(r.personal_leave_days)}</td>
        <td style="text-align: center;">${r.official_leave_times || 0}</td>
        <td style="text-align: center;">${formatDays(r.official_leave_days)}</td>
        <td style="text-align: center;" class="${r.absent_days > 0 ? 'text-red' : ''}">${formatDays(r.absent_days)}</td>
      </tr>
    `).join('');

    // ใช้ A4 Landscape สำหรับตารางสรุปที่มีคอลัมน์เยอะ
    const printStyles = `
      @font-face {
        font-family: 'Sarabun';
        src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
      @font-face {
        font-family: 'Sarabun';
        src: url('${API_BASE}fonts/THSarabun-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @page { 
        size: A4 landscape; 
        margin: 1cm; 
      }
      body { 
        font-family: 'Sarabun', sans-serif; 
        font-size: 18pt;
        color: #000;
      }
      .header { 
        text-align: center; 
        margin-bottom: 15px; 
        font-size: 18pt;
        font-weight: bold;
      }
      .logo { 
        height: 60px; 
        margin-bottom: 5px;
      }
      table { 
        width: 100%; 
        border-collapse: collapse; 
        font-size: 18pt;
        margin-bottom: 20px;
      }
      th, td { 
        border: 1px solid #000; 
        padding: 4px; 
        vertical-align: middle;
        height: auto;
        line-height: 1.2;
      }
      th { 
        background-color: #f0f0f0; 
        font-weight: bold;
        text-align: center;
      }
      .text-red { color: red; font-weight: bold; }
      @media print {
        body { margin: 0; }
        .header { -webkit-print-color-adjust: exact; }
      }
    `;

    const content = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div>${organization?.name || 'หน่วยงาน'}</div>
        <div>รายงานสรุปการลงเวลาปฏิบัติราชการ</div>
        <div style="font-size: 18pt; font-weight: normal;">${dateTitle}</div>
      </div>
      <table>
        <thead>
          <tr>
            <th rowspan="2" style="width: 5%;">ที่</th>
            <th rowspan="2" style="width: 17%;">ชื่อ-สกุล</th>
            <th rowspan="2" style="width: 15%;">กลุ่มสาระฯ</th>
            <th rowspan="2" style="width: 5%;">สาย<br>(ครั้ง)</th>
            <th colspan="2">ลาป่วย</th>
            <th colspan="2">ลากิจ</th>
            <th colspan="2">ราชการ</th>
            <th rowspan="2" style="width: 5%;">ขาด<br>(วัน)</th>
          </tr>
          <tr>
            <th style="width: 5%;">ครั้ง</th><th style="width: 5%;">วัน</th>
            <th style="width: 5%;">ครั้ง</th><th style="width: 5%;">วัน</th>
            <th style="width: 5%;">ครั้ง</th><th style="width: 5%;">วัน</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${getSignatureSection()}
    `;
    printReport(content, 'รายงานสรุป');
  };

  const handleOpenDetails = async (employee) => {
    setCurrentDetailEmployee(employee);
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const startYear = new Date(dateRange.from).getFullYear();
      const endYear = new Date(dateRange.to).getFullYear();
      const years = [...new Set([startYear, endYear])];

      const [attendanceData, ...holidayResults] = await Promise.all([
        api.getReports({
          type: 'daily',
          date_from: dateRange.from,
          date_to: dateRange.to,
          employee_id: employee.employee_id
        }),
        ...years.map(y => api.getHolidays(y))
      ]);

      const holidayDates = new Set(holidayResults.flat().map(h => h.holiday_date));
      const attendanceMap = {};
      attendanceData.forEach(r => { if (r.work_date) attendanceMap[r.work_date] = r; });

      const fullDetailData = [];
      let curr = new Date(dateRange.from);
      const end = new Date(dateRange.to);
      curr.setHours(12, 0, 0, 0); 
      end.setHours(12, 0, 0, 0);

      while (curr <= end) {
        const dateStr = curr.toISOString().split('T')[0];
        const dayOfWeek = curr.getDay(); 
        const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6);
        const isHoliday = holidayDates.has(dateStr);

        if ((!isWeekend && !isHoliday) || attendanceMap[dateStr]) {
          if (attendanceMap[dateStr]) {
            const record = attendanceMap[dateStr];
            const isCheckInMissing = !record.check_in || record.check_in === '-' || record.check_in === '00:00:00';
             if (isCheckInMissing && record.status === 'ปกติ') {
               fullDetailData.push({ ...record, status: 'ไม่ลงเวลาเข้า' });
             } else {
               fullDetailData.push(record);
             }
          } else {
            fullDetailData.push({
              work_date: dateStr,
              check_in: null,
              check_out: null,
              status: 'ไม่มาปฏิบัติงาน',
              notes: '',
              attendance_notes: ''
            });
          }
        }
        curr.setDate(curr.getDate() + 1);
      }
      fullDetailData.sort((a, b) => new Date(b.work_date) - new Date(a.work_date));
      setDetailData(fullDetailData);
    } catch (error) {
      console.error("Error fetching details:", error);
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  };

  // --- 2. พิมพ์รายงานรายละเอียด (รายบุคคล) ---
  const generateDetailPrint = () => {
    if (!currentDetailEmployee || detailData.length === 0) return;

    const dateTitle = dateRange.from === dateRange.to ? formatDateThai(dateRange.from) : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`;
    const logoHtml = organization?.logo ? `<img src="${API_BASE}${organization.logo}" class="logo">` : '';

    const tableRows = detailData.map((r) => `
      <tr>
        <td style="text-align: center;">${formatDateThai(r.work_date)}</td>
        <td style="text-align: center;">${formatTime(r.check_in)}</td>
        <td style="text-align: center;">${formatTime(r.check_out)}</td>
        <td style="text-align: center;">${r.status}</td>
        <td>${r.notes || r.attendance_notes || '-'}</td>
      </tr>
    `).join('');

    const printStyles = `
      @font-face {
        font-family: 'Sarabun';
        src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype');
        font-weight: 300;
        font-style: normal;
      }
      @font-face {
        font-family: 'Sarabun';
        src: url('${API_BASE}fonts/THSarabun-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }
      @page { 
        size: A4 portrait; 
        margin: 1.5cm; 
      }
      body { 
        font-family: 'Sarabun', sans-serif; 
        font-size: 18pt; 
        color: #000;
      }
      .header { 
        text-align: center; 
        margin-bottom: 15px; 
        font-size: 18pt;
        font-weight: bold;
        border-bottom: 2px solid #000;
        padding-bottom: 10px;
      }
      .logo { 
        height: 80px; 
        margin-bottom: 10px; 
      }
      table { 
        width: 100%; 
        border-collapse: collapse; 
        margin-top: 10px; 
        margin-bottom: 20px;
      }
      th, td { 
        border: 1px solid #000; 
        padding: 6px 4px; 
        vertical-align: top; 
        height: auto;
        line-height: 1.3;
      }
      th { 
        background-color: #f0f0f0; 
        text-align: center; 
        font-weight: bold;
      }
      .emp-info { 
        margin-bottom: 15px; 
        font-weight: bold;
        font-size: 18pt;
      }
      @media print {
        body { margin: 0; }
        .header { -webkit-print-color-adjust: exact; }
      }
    `;

    const content = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div>${organization?.name || 'หน่วยงาน'}</div>
        <div>รายงานรายละเอียดการลงเวลา</div>
        <div style="font-size:18pt; font-weight:normal">${dateTitle}</div>
      </div>
      <div class="emp-info">
        ชื่อ-สกุล: ${currentDetailEmployee.first_name} ${currentDetailEmployee.last_name} <br>
        กลุ่มสาระฯ: ${currentDetailEmployee.department || '-'}
      </div>
      <table>
        <thead>
          <tr>
            <th style="width: 20%;">วันที่</th>
            <th style="width: 15%;">เวลาเข้า</th>
            <th style="width: 15%;">เวลาออก</th>
            <th style="width: 20%;">สถานะ</th>
            <th style="width: 30%;">หมายเหตุ</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${getSignatureSection()}
    `;
    printReport(content, `รายละเอียด_${currentDetailEmployee.first_name}`);
  };

  const handleDownloadIndividual = (type, employee) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${API_BASE}individual-notify-download-docx?type=${type}&employee_id=${employee.employee_id}&date_from=${dateRange.from}&date_to=${dateRange.to}&token=${token}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">รายงานสรุป</h1>
        <button onClick={generatePrintReport} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2">
          <Printer className="h-5 w-5" /> พิมพ์ตารางสรุป
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded shadow p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <input type="date" value={dateRange.from} onChange={e => setDateRange({...dateRange, from: e.target.value})} className="border p-2 rounded" />
        <input type="date" value={dateRange.to} onChange={e => setDateRange({...dateRange, to: e.target.value})} className="border p-2 rounded" />
        <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)} className="border p-2 rounded">
          <option value="">พนักงานทั้งหมด</option>
          {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
        </select>
        <select value={filterMode} onChange={e => setFilterMode(e.target.value)} className="border p-2 rounded">
          <option value="all">แสดงทั้งหมด</option>
          <option value="late_gt_4">สายเกิน 4 ครั้ง</option>
          <option value="leave_gt_3">ลาเกิน 3 ครั้ง</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded shadow overflow-x-auto">
        {loading ? <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500"/></div> : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan="2" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r">ชื่อ-สกุล</th>
                <th rowSpan="2" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r">กลุ่มสาระฯ</th>
                <th rowSpan="2" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase border-r">สาย</th>
                <th colSpan="2" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase border-r border-b">ลาป่วย</th>
                <th colSpan="2" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase border-r border-b">ลากิจ</th>
                <th colSpan="2" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase border-r border-b">ราชการ</th>
                <th rowSpan="2" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase border-r">ขาด</th>
                <th rowSpan="2" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase border-r">ดาวน์โหลดบันทึก</th>
                <th rowSpan="2" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">จัดการ</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-center text-xs text-gray-500 border-r border-b">ครั้ง</th>
                <th className="px-2 py-2 text-center text-xs text-gray-500 border-r border-b">วัน</th>
                <th className="px-2 py-2 text-center text-xs text-gray-500 border-r border-b">ครั้ง</th>
                <th className="px-2 py-2 text-center text-xs text-gray-500 border-r border-b">วัน</th>
                <th className="px-2 py-2 text-center text-xs text-gray-500 border-r border-b">ครั้ง</th>
                <th className="px-2 py-2 text-center text-xs text-gray-500 border-r border-b">วัน</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.length === 0 ? <tr><td colSpan="12" className="p-4 text-center text-gray-500">ไม่พบข้อมูล</td></tr> : 
               reports.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-4 text-sm font-medium text-gray-900 border-r">{r.first_name} {r.last_name}</td>
                  <td className="px-3 py-4 text-sm font-medium text-gray-900 border-r">{r.department || '-'}</td>
                  <td className="px-3 py-4 text-sm text-center text-red-600 font-bold border-r">{r.late_days || '-'}</td>
                  
                  <td className="px-3 py-4 text-sm text-center text-gray-500 border-r">{r.sick_leave_times || '-'}</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500 border-r">{formatDays(r.sick_leave_days)}</td>
                  
                  <td className="px-3 py-4 text-sm text-center text-gray-500 border-r">{r.personal_leave_times || '-'}</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500 border-r">{formatDays(r.personal_leave_days)}</td>
                  
                  <td className="px-3 py-4 text-sm text-center text-gray-500 border-r">{r.official_leave_times || '-'}</td>
                  <td className="px-3 py-4 text-sm text-center text-gray-500 border-r">{formatDays(r.official_leave_days)}</td>
                  
                  <td className="px-3 py-4 text-sm text-center text-red-600 font-bold border-r">{formatDays(r.absent_days) || '-'}</td>
                  
                  <td className="px-3 py-4 text-center space-x-1 whitespace-nowrap border-r">
                    <button 
                      onClick={() => handleDownloadIndividual('late', r)}
                      disabled={!r.late_days}
                      className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded disabled:opacity-30 hover:bg-red-200 transition-colors"
                      title="บันทึกมาสาย"
                    >
                      {downloadLoading.active && downloadLoading.employeeId === r.employee_id && downloadLoading.type === 'late' ? <Loader2 className="h-3 w-3 animate-spin"/> : <FileDown className="h-3 w-3"/>}
                    </button>
                    <button 
                      onClick={() => handleDownloadIndividual('leave', r)}
                      disabled={!r.sick_leave_times && !r.personal_leave_times}
                      className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded disabled:opacity-30 hover:bg-purple-200 transition-colors"
                      title="บันทึกการลา"
                    >
                      {downloadLoading.active && downloadLoading.employeeId === r.employee_id && downloadLoading.type === 'leave' ? <Loader2 className="h-3 w-3 animate-spin"/> : <FileDown className="h-3 w-3"/>}
                    </button>
                    <button 
                      onClick={() => handleDownloadIndividual('absent', r)}
                      disabled={!r.absent_days}
                      className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded disabled:opacity-30 hover:bg-gray-300 transition-colors"
                      title="บันทึกขาดงาน"
                    >
                      {downloadLoading.active && downloadLoading.employeeId === r.employee_id && downloadLoading.type === 'absent' ? <Loader2 className="h-3 w-3 animate-spin"/> : <FileDown className="h-3 w-3"/>}
                    </button>
                  </td>

                  <td className="px-3 py-4 text-center whitespace-nowrap">
                    <button 
                      onClick={() => handleOpenDetails(r)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 inline-flex items-center transition-colors"
                      title="ดูรายละเอียด"
                    >
                      <List className="h-3 w-3 mr-1"/> รายละเอียด
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showDetailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  รายละเอียดการลงเวลา: {currentDetailEmployee?.first_name} {currentDetailEmployee?.last_name}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  ช่วงวันที่ {formatDateThai(dateRange.from)} - {formatDateThai(dateRange.to)}
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={generateDetailPrint}
                  className="bg-green-500 text-white px-3 py-1.5 rounded hover:bg-green-600 flex items-center space-x-1 transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  <span>พิมพ์</span>
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-500 hover:text-red-500 p-1 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto border border-gray-200 rounded-md">
              {detailLoading ? (
                <div className="flex flex-col justify-center items-center h-48">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-2" />
                  <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">เข้างาน</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ออกงาน</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">หมายเหตุ</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {detailData.length > 0 ? detailData.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2 text-sm text-gray-900">{formatDateThai(r.work_date)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{formatTime(r.check_in)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{formatTime(r.check_out)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">{r.notes || r.attendance_notes || '-'}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          ไม่พบข้อมูลการลงเวลา
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 font-medium transition-colors"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SummaryReport;